import { useState, useEffect, useCallback } from 'react';

// Shared drill-down state machine for report pages: tracks which row/column
// is expanded, a search box, and cursor-based "load more" pagination — all
// without re-fetching the parent report or reloading the page.
//
// `fetchFn(params)` must return an axios-style promise resolving to
// `{ data: { data: { leads, total?, pagination?: { nextCursor, total? } } } }`
// (matches both leadService.getLeads and companyService.getWorkspaceLeadsPreview).
export function useDrillDown(fetchFn) {
  const [expanded, setExpanded] = useState(null); // { key, title, params }
  const [search, setSearch] = useState('');
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const toggle = useCallback((key, title, params) => {
    setSearch('');
    setExpanded(prev => (prev?.key === key ? null : { key, title, params }));
  }, []);

  const close = useCallback(() => setExpanded(null), []);

  useEffect(() => {
    if (!expanded) { setLeads([]); setTotal(null); setCursor(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    fetchFn({ ...expanded.params, search: search || undefined, limit: 50 })
      .then((res) => {
        if (cancelled) return;
        const d = res?.data?.data || {};
        setLeads(d.leads || []);
        setTotal(typeof d.total === 'number' ? d.total : (d.pagination?.total ?? null));
        setCursor(d.pagination?.nextCursor || null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded?.key, expanded?.params, search]);

  const loadMore = useCallback(() => {
    if (!expanded || !cursor) return;
    setLoadingMore(true);
    fetchFn({ ...expanded.params, search: search || undefined, limit: 50, cursor })
      .then((res) => {
        const d = res?.data?.data || {};
        setLeads((prev) => [...prev, ...(d.leads || [])]);
        setCursor(d.pagination?.nextCursor || null);
      })
      .finally(() => setLoadingMore(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, cursor, search]);

  return { expanded, toggle, close, search, setSearch, leads, total, loading, loadingMore, hasMore: !!cursor, loadMore };
}
